import { useEffect, useRef, useState } from 'react';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';

// Masks: currency allows digits + a single decimal point with up to two
// decimal places; integer allows digits only. Both accept the empty string so
// a cleared field survives mid-edit.
const CURRENCY_MASK = /^\d*\.?\d{0,2}$/;
const INTEGER_MASK = /^\d*$/;

export interface MoneyInputProps {
  /** Committed numeric value. 0 renders as empty (placeholder shown). */
  value: number;
  /** Called with the parsed value on every accepted keystroke. */
  onChange: (value: number) => void;
  ariaLabel: string;
  mode?: 'currency' | 'integer';
  placeholder?: string;
  disabled?: boolean;
  className?: string;
}

function normalize(value: number, mode: 'currency' | 'integer'): string {
  if (!(value > 0)) return '';
  return mode === 'integer' ? String(Math.round(value)) : value.toFixed(2);
}

/**
 * A text box with a currency or integer mask — no number-spinner arrows, and a
 * currency-style commit (blurring "12" shows "12.00"). The raw string is held
 * locally so mid-edit states ("", a trailing ".") survive; the parsed value is
 * lifted up for the bill math. Mirrors the NumberCell pattern in
 * JobCardFieldsCard, extracted here so the equipment tables share one input.
 */
export function MoneyInput({
  value,
  onChange,
  ariaLabel,
  mode = 'currency',
  placeholder = mode === 'integer' ? '0' : '0.00',
  disabled = false,
  className,
}: MoneyInputProps) {
  const mask = mode === 'integer' ? INTEGER_MASK : CURRENCY_MASK;
  const [text, setText] = useState(() => normalize(value, mode));
  // Guards the resync effect from clobbering an in-progress edit whose parsed
  // value happens to equal `value` (e.g. typing "12." while value is 12).
  const editing = useRef(false);

  useEffect(() => {
    if (editing.current) return;
    setText(normalize(value, mode));
  }, [value, mode]);

  const parse = (raw: string): number => {
    const n = parseFloat(raw);
    return Number.isNaN(n) || n < 0 ? 0 : n;
  };

  const handleChange = (raw: string) => {
    if (!mask.test(raw)) return; // reject keystrokes that break the mask
    editing.current = true;
    setText(raw);
    onChange(parse(raw));
  };

  const handleBlur = () => {
    editing.current = false;
    // Normalize the display on commit ("12" → "12.00", "0" / "" → placeholder).
    setText(normalize(parse(text), mode));
  };

  return (
    <Input
      type="text"
      inputMode={mode === 'integer' ? 'numeric' : 'decimal'}
      aria-label={ariaLabel}
      value={text}
      placeholder={placeholder}
      disabled={disabled}
      onChange={(e) => handleChange(e.target.value)}
      onBlur={handleBlur}
      className={cn('h-7 text-sm tabular-nums text-right', className)}
    />
  );
}
